import { getGlobalHeadersAsObject } from "../global-headers";

export type CommsMatrixStats = {
	sent: number;
	responses: number;
	interested: number;
};

export const getCommsMatrixStats = ({
	assignmentId,
}: {
	assignmentId: number;
}): Promise<CommsMatrixStats> => {
	return fetch(`/api/v2/assignment/${assignmentId}/stats_communication_matrix/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

type BulkField = "interested" | "sent" | "responses";

const getCommsMatrixBulk = (
	field: BulkField,
	assignmentIds: number[]
): Promise<Record<number, number>> => {
	if (assignmentIds.length === 0) return Promise.resolve({});
	const ids = assignmentIds.join(",");
	return fetch(`/api/v2/comm_matrix_stats_bulk/${field}/?ids=${ids}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Fetch all four comm-matrix fields for N assignments in parallel.
 * Returns `{ [assignmentId]: CommsMatrixStats }` with zero-filled defaults
 * so callers can index safely before the fetch resolves.
 */
export const getCommsMatrixStatsBulk = async (
	assignmentIds: number[]
): Promise<Record<number, CommsMatrixStats>> => {
	if (assignmentIds.length === 0) return {};
	const [interested, sent, responses] = await Promise.all([
		getCommsMatrixBulk("interested", assignmentIds),
		getCommsMatrixBulk("sent", assignmentIds),
		getCommsMatrixBulk("responses", assignmentIds),
	]);
	const out: Record<number, CommsMatrixStats> = {};
	for (const id of assignmentIds) {
		out[id] = {
			sent: sent[id] ?? 0,
			responses: responses[id] ?? 0,
			interested: interested[id] ?? 0,
		};
	}
	return out;
};

/**
 * Bulk-fetch just the `responses` field. Useful for dashboards that only need
 * response counts (e.g. the recruitment funnel tab).
 */
export const getCommsMatrixResponsesBulk = (
	assignmentIds: number[]
): Promise<Record<number, number>> => getCommsMatrixBulk("responses", assignmentIds);
